/**
 * Maps hub data and calculator results to IRS Form 8824 fields.
 * Form 8824 is used to report like-kind exchanges under IRC §1031.
 */

export interface Form8824Field {
  line: string
  label: string
  value: string | number | null
  source: 'sales_hub' | 'identification_hub' | 'purchase_hub' | 'disbursement_hub' | 'calculator' | 'manual'
  sourceLabel: string
  filled: boolean
}

export interface Form8824Data {
  fields: Form8824Field[]
  filledCount: number
  totalCount: number
}

export function mapToForm8824(
  salesData: any,
  _identificationData: any,
  purchaseData: any,
  calculatorResults: any
): Form8824Data {
  const calc = calculatorResults?.results || {}
  const calcInput = calculatorResults?.input || {}

  const fields: Form8824Field[] = [
    // Part I — Information on the Like-Kind Exchange
    {
      line: '1',
      label: 'Description of like-kind property given up',
      value: salesData?.relinquished?.addressOrLegalDescription || null,
      source: 'sales_hub',
      sourceLabel: 'Sales Hub',
      filled: !!salesData?.relinquished?.addressOrLegalDescription,
    },
    {
      line: '2',
      label: 'Description of like-kind property received',
      value: purchaseData?.replacement?.addressOrLegalDescription || null,
      source: 'purchase_hub',
      sourceLabel: 'Purchase Hub',
      filled: !!purchaseData?.replacement?.addressOrLegalDescription,
    },
    {
      line: '3',
      label: 'Date like-kind property given up was originally acquired',
      value: null, // Requires manual input — original purchase date
      source: 'manual',
      sourceLabel: 'Manual Entry',
      filled: false,
    },
    {
      line: '4',
      label: 'Date like-kind property given up was transferred',
      value: salesData?.relinquished?.closingDate || null,
      source: 'sales_hub',
      sourceLabel: 'Sales Hub',
      filled: !!salesData?.relinquished?.closingDate,
    },
    {
      line: '5',
      label: 'Date replacement property was identified',
      value: (() => {
        const d = localStorage.getItem('identification_hub_submitted_date')
        return d ? new Date(d).toLocaleDateString() : null
      })(),
      source: 'identification_hub',
      sourceLabel: 'Identification Hub',
      filled: !!localStorage.getItem('identification_hub_submitted_date'),
    },
    {
      line: '6',
      label: 'Date replacement property was received',
      value: purchaseData?.replacement?.closingDate || null,
      source: 'purchase_hub',
      sourceLabel: 'Purchase Hub',
      filled: !!purchaseData?.replacement?.closingDate,
    },
    {
      line: '7',
      label: 'Was the exchange made with a related party?',
      value: 'No',
      source: 'manual',
      sourceLabel: 'Default',
      filled: true,
    },

    // Part II (if related party — skip for now)

    // Part III — Realized Gain or Loss, Recognized Gain, and Basis of Like-Kind Property Received
    {
      line: '12',
      label: 'Fair market value (FMV) of like-kind property received',
      value: calcInput?.replacementPropertyCost || null,
      source: 'calculator',
      sourceLabel: 'Calculator',
      filled: !!calcInput?.replacementPropertyCost,
    },
    {
      line: '13',
      label: 'Adjusted basis of like-kind property given up',
      value: calc?.adjustedBasis || null,
      source: 'calculator',
      sourceLabel: 'Calculator',
      filled: !!calc?.adjustedBasis,
    },
    {
      line: '14',
      label: 'Net amount realized on the exchange',
      value: calcInput ? (calcInput.sellingPrice - calcInput.sellingExpenses) : null,
      source: 'calculator',
      sourceLabel: 'Calculator',
      filled: !!(calcInput?.sellingPrice),
    },
    {
      line: '15',
      label: 'Cash received (or paid)',
      value: calc?.boot?.netCash ?? null,
      source: 'calculator',
      sourceLabel: 'Calculator',
      filled: calc?.boot?.netCash !== undefined,
    },
    {
      line: '16',
      label: 'FMV of other (not like-kind) property received',
      value: calc?.boot?.totalBoot || 0,
      source: 'calculator',
      sourceLabel: 'Calculator',
      filled: calc?.boot !== undefined,
    },
    {
      line: '17',
      label: 'Gain recognized on the exchange',
      value: calc?.recognizedGain ?? null,
      source: 'calculator',
      sourceLabel: 'Calculator',
      filled: calc?.recognizedGain !== undefined,
    },
    {
      line: '18',
      label: 'Deferred gain',
      value: calc?.deferredGain ?? null,
      source: 'calculator',
      sourceLabel: 'Calculator',
      filled: calc?.deferredGain !== undefined,
    },
    {
      line: '19',
      label: 'Basis of like-kind property received',
      value: calc?.replacementPropertyBasis ?? null,
      source: 'calculator',
      sourceLabel: 'Calculator',
      filled: calc?.replacementPropertyBasis !== undefined,
    },
  ]

  const filledCount = fields.filter(f => f.filled).length

  return {
    fields,
    filledCount,
    totalCount: fields.length,
  }
}

/**
 * Format a field value for display.
 */
export function formatFieldValue(value: string | number | null): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }
  return String(value)
}
